import React, { useEffect, useState } from "react";
import { FirebaseError } from "firebase/app";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import BrandMark from "./BrandMark";
import {
  benefits,
  coach,
  hero,
  offer,
  price,
} from "../content/landingCopy";
import {
  COURSE_CONTENT_NAME,
  COURSE_LIST_CURRENCY,
  COURSE_LIST_VALUE,
} from "../metaCourse";
import {
  COURSE_PRICE_ID,
  acceptPublicProgram,
  fetchCoursePurchaseStatus,
  fetchProgramEnrollmentStatus,
  startCourseCheckout,
} from "../publicProgram";
import { metaPixelService } from "../services/metaPixelService";
import {
  CONSENT_EVENT,
  type CookiePrefs,
  readMarketingConsent,
} from "../utils/cookieConsent";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=app.peak.peakandroid";
const APP_STORE_URL = "https://apps.apple.com/th/app/peak/id6784633966";

const parseLocal = (value: string) => new Date(`${value}T00:00:00`);

const toDateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const formatLongDate = (value: string) =>
  parseLocal(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/** Mondays after today. Today is excluded even when it is a Monday. */
function upcomingMondays(count = 52): string[] {
  const dates: string[] = [];
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  cursor.setDate(cursor.getDate() + ((8 - cursor.getDay()) % 7 || 7));
  for (let i = 0; i < count; i += 1) {
    dates.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  return dates;
}

function defaultStartDate(): string {
  return upcomingMondays(1)[0];
}

function isFutureMonday(value: string): boolean {
  return upcomingMondays().includes(value);
}

function callableMessage(error: unknown, fallback: string): string {
  if (error instanceof FirebaseError) {
    return error.message.replace(/^Firebase: /, "") || fallback;
  }
  return fallback;
}

interface PublicProgramViewProps {
  orgId: string;
  programId: string;
}

const buyButtonClass =
  "inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold bg-primary text-primary-content rounded-md font-grotesk shadow-[0_6px_0_#005a7a] hover:-translate-y-0.5 hover:shadow-[0_8px_0_#005a7a] active:translate-y-1 active:shadow-[0_2px_0_#005a7a] transition-all disabled:opacity-60 disabled:pointer-events-none";

const PublicProgramView: React.FC<PublicProgramViewProps> = ({
  orgId,
  programId,
}) => {
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptResult, setAcceptResult] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  const [enrollmentChecked, setEnrollmentChecked] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutSessionId = searchParams.get("checkout_session_id");
  const [marketing, setMarketing] = useState(readMarketingConsent);
  const [purchased, setPurchased] = useState<boolean | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchCoursePurchaseStatus(programId);
        if (!cancelled) setPurchased(result.purchased);
      } catch (error) {
        if (!cancelled) setPurchased(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchProgramEnrollmentStatus(orgId, programId);
        if (cancelled || !status.enrolled) return;
        setEnrolled(true);
        if (status.startDate) setStartDate(status.startDate);
        setAcceptResult(
          `You're enrolled, starting ${formatLongDate(status.startDate || startDate)}${
            status.sessionCount ? ` · ${status.sessionCount} session${status.sessionCount === 1 ? "" : "s"}` : ""
          }.`,
        );
      } catch (error) {
        // Not fatal — the Accept course flow still works and is idempotent.
      } finally {
        if (!cancelled) setEnrollmentChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, programId]);

  useEffect(() => {
    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<CookiePrefs>).detail;
      if (detail && typeof detail.marketing === "boolean") {
        setMarketing(detail.marketing);
        return;
      }
      setMarketing(readMarketingConsent());
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_EVENT, onConsent);
  }, []);

  useEffect(() => {
    if (!checkoutSessionId || !marketing) return;
    const sentKey = `peak_education_meta_purchase_${checkoutSessionId}`;
    const stripSession = () => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("checkout_session_id");
        return next;
      }, { replace: true });
    };
    if (localStorage.getItem(sentKey)) {
      stripSession();
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timer = 0;

    const poll = async () => {
      try {
        const status = await fetchCoursePurchaseStatus(programId, checkoutSessionId);
        if (cancelled) return;
        if (
          status.purchased
          && status.eventId === checkoutSessionId
          && typeof status.value === "number"
          && status.currency
        ) {
          metaPixelService.track(
            "Purchase",
            {
              content_name: COURSE_CONTENT_NAME,
              content_ids: [COURSE_PRICE_ID],
              content_type: "product",
              value: status.value,
              currency: status.currency.toUpperCase(),
            },
            status.eventId,
          );
          localStorage.setItem(sentKey, "1");
          stripSession();
          return;
        }
      } catch {
        // The webhook may not have written the purchase yet.
      }
      attempts += 1;
      if (!cancelled && attempts < 8) {
        timer = window.setTimeout(poll, 1500);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [checkoutSessionId, marketing, programId, setSearchParams]);

  const handleBuy = async () => {
    if (!user) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const idToken = await user.getIdToken();
      const url = await startCourseCheckout({
        orgId,
        programId,
        priceId: COURSE_PRICE_ID,
        idToken,
        email: user.email,
      });
      metaPixelService.track("InitiateCheckout", {
        content_name: COURSE_CONTENT_NAME,
        content_ids: [COURSE_PRICE_ID],
        content_type: "product",
        value: COURSE_LIST_VALUE,
        currency: COURSE_LIST_CURRENCY,
      });
      window.location.href = url;
    } catch (error) {
      setCheckoutError(
        error instanceof Error ? error.message : "Couldn't start checkout",
      );
      setCheckoutLoading(false);
    }
  };

  const handleAccept = async (date: string) => {
    if (!isFutureMonday(date)) {
      setAcceptError("Choose a future Monday");
      return;
    }
    setStartDate(date);
    setAccepting(true);
    setAcceptError(null);
    setAcceptResult(null);
    try {
      const result = await acceptPublicProgram(orgId, programId, date);
      setEnrolled(true);
      setShowDownloadDialog(true);
      if (result.alreadyAssigned) {
        setAcceptResult(
          `You already have this program, starting ${formatLongDate(result.startDate)}.`,
        );
      } else {
        setAcceptResult(
          `Added to your schedule from ${formatLongDate(result.startDate)} · ${result.sessionCount} session${result.sessionCount === 1 ? "" : "s"}.`,
        );
      }
    } catch (error) {
      setAcceptError(callableMessage(error, "Couldn't add this program"));
    } finally {
      setAccepting(false);
    }
  };

  if (purchased === null || !enrollmentChecked) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="loading loading-spinner loading-md text-primary" />
      </div>
    );
  }

  return (
    <>
      {enrolled ? (
        <EnrolledState
          message={acceptResult}
          startDate={startDate}
        />
      ) : purchased ? (
        <StartDateStep
          startDate={startDate}
          onStartDate={(value) => {
            setStartDate(value);
            setAcceptResult(null);
            setAcceptError(null);
          }}
          onAccept={handleAccept}
          accepting={accepting}
          error={acceptError}
        />
      ) : (
        <ProgramOverview
          onBuy={handleBuy}
          buying={checkoutLoading}
          error={checkoutError}
        />
      )}
      {showDownloadDialog && (
        <DownloadAppDialog onClose={() => setShowDownloadDialog(false)} />
      )}
    </>
  );
};

function ProgramOverview({
  onBuy,
  buying,
  error,
}: {
  onBuy: () => void;
  buying: boolean;
  error: string | null;
}) {
  const [priceAmount, priceCurrency] = price.split(" ");

  return (
    <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-16">
      <div>
        <p className="inline-flex items-center gap-2 rounded-md border border-base-300 bg-base-100 px-3 py-1.5 font-mono text-[11px] sm:text-xs uppercase tracking-widest text-base-content/70">
          <span className="w-2 h-2 rounded-full bg-volt ring-2 ring-blue-900" />
          {hero.eyebrow}
        </p>
        <h2 className="mt-5 text-4xl sm:text-6xl font-extrabold font-heading leading-[0.95] tracking-tight text-pretty">
          {hero.title}
        </h2>
        <p className="mt-5 max-w-xl text-lg text-base-content/70 font-grotesk leading-relaxed">
          {hero.subtitle}
        </p>
        <div className="mt-6 flex items-center gap-3">
          <img
            src={coach.photo}
            alt=""
            className="w-11 h-11 rounded-full object-cover object-[center_18%] ring-4 ring-volt/40"
          />
          <p className="font-grotesk text-sm text-base-content/70">{hero.trustLine}</p>
        </div>
        <ul className="mt-8 flex flex-col gap-3">
          {benefits.map((benefit) => (
            <li key={benefit.title} className="rounded-md border border-base-300 px-5 py-4">
              <p className="font-grotesk font-semibold">{benefit.title}</p>
              <p className="mt-1 text-sm text-base-content/70 font-grotesk">{benefit.body}</p>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-md bg-base-100 border-2 border-blue-900 shadow-[10px_10px_0_#003d55] p-8 sm:p-10">
        <p className="font-mono text-xs uppercase tracking-widest text-primary text-center">
          {offer.subtitle}
        </p>
        <h3 className="text-3xl font-extrabold font-heading text-center mt-3">
          {offer.title}
        </h3>
        <p className="text-center mt-6">
          <span className="font-mono text-7xl font-semibold tracking-tighter">{priceAmount}</span>
          <span className="font-mono text-lg text-base-content/60 ml-2">{priceCurrency}</span>
        </p>
        <ul className="mt-8 flex flex-col gap-3">
          {offer.includes.map((item) => (
            <li key={item} className="flex gap-3 font-grotesk">
              <span aria-hidden="true" className="mt-0.5 text-primary">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <button type="button" onClick={onBuy} disabled={buying} className={`${buyButtonClass} mt-9 w-full`}>
          {buying ? "Redirecting…" : "Buy program"}
        </button>
        {error && <p className="mt-3 text-sm text-error font-grotesk text-center">{error}</p>}
      </div>
    </div>
  );
}

function StartDateStep({
  startDate,
  onStartDate,
  onAccept,
  accepting,
  error,
}: {
  startDate: string;
  onStartDate: (value: string) => void;
  onAccept: (date: string) => void;
  accepting: boolean;
  error: string | null;
}) {
  const mondays = upcomingMondays();
  const selected = mondays.includes(startDate) ? startDate : mondays[0];

  return (
    <form
      className="mx-auto flex w-full max-w-sm flex-col gap-4 py-10"
      onSubmit={(event) => {
        event.preventDefault();
        onAccept(selected);
      }}
    >
      <label>
        <span className="block text-sm font-semibold font-grotesk mb-2">Start date</span>
        <select
          value={selected}
          onChange={(event) => onStartDate(event.target.value)}
          className="w-full rounded-md border border-base-300 bg-base-100 px-4 py-3 text-base font-grotesk focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary"
        >
          {mondays.map((date) => (
            <option key={date} value={date}>
              Monday, {formatLongDate(date)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="submit"
        disabled={accepting || !isFutureMonday(selected)}
        className={buyButtonClass}
      >
        {accepting ? "Adding…" : "Accept program"}
      </button>
      {error && <p className="text-sm text-error font-grotesk">{error}</p>}
    </form>
  );
}

function EnrolledState({
  message,
  startDate,
}: {
  message: string | null;
  startDate: string;
}) {
  return (
    <div className="max-w-lg py-6">
      <p className="font-mono text-xs uppercase tracking-widest text-primary">You're in</p>
      <h2 className="mt-2 text-4xl font-extrabold font-heading tracking-tight">You're enrolled</h2>
      <p className="mt-3 text-base-content/80 font-grotesk leading-relaxed">
        {message || `Starting ${formatLongDate(startDate)}.`}
      </p>
      <p className="mt-3 text-base-content/70 font-grotesk">
        Follow this course in PEAK. Sessions, training load, and your diary live in the app.
      </p>
      <div className="mt-8 flex flex-col gap-2 sm:flex-row">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={buyButtonClass}
        >
          Download on the App Store
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold bg-base-content text-base-100 rounded-md font-grotesk"
        >
          Get it on Google Play
        </a>
      </div>
    </div>
  );
}

function DownloadAppDialog({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-peak-title"
        className="relative z-10 w-full sm:max-w-md rounded-md bg-base-100 border border-base-300 shadow-xl px-6 py-6"
      >
        <BrandMark />
        <h3 id="download-peak-title" className="mt-4 text-lg font-semibold font-heading">
          Download the app
        </h3>
        <p className="mt-2 text-sm text-base-content/70 font-grotesk">
          You&apos;re enrolled. Follow this course in PEAK^ — sessions, training
          load, and your diary live in the app.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium bg-primary text-primary-content rounded-md hover:bg-primary/90 transition-colors font-grotesk"
          >
            Download on the App Store
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium bg-base-content text-base-100 rounded-md hover:bg-base-content/90 transition-colors font-grotesk"
          >
            Get it on Google Play
          </a>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-md border border-base-300 hover:bg-base-200 transition-colors font-grotesk"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

export default PublicProgramView;
